import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit2, Trash2, X, Eye, EyeOff, Loader2 } from 'lucide-react';

export default function StaffAccessControl() {
  const [staffList, setStaffList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [tableLoading, setTableLoading] = useState(false);
  const itemsPerPage = 5;

  // Modal & Form States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingStaffId, setEditingStaffId] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '', email: '', mobile: '', gender: 'Male', role: 'counsellor', password: '',
    permissions: { crm: false, students: false, courses: false, fees: false, exams: false, notifications: false, analytics: false, access: false }
  });

  const modulesCheckboxConfig = [
    { id: 'crm', name: 'CRM & Enquiries' },
    { id: 'students', name: 'Student Management' },
    { id: 'courses', name: 'Course Management' },
    { id: 'fees', name: 'Fee Management' },
    { id: 'exams', name: 'Exams & Projects' },
    { id: 'notifications', name: 'Push Notifications' },
    { id: 'analytics', name: 'Reports & Analytics' },
    { id: 'access', name: 'Staff Access Control' },
  ];

  // 🔄 INTEGRATE LIVE BACKEND FETCH
  useEffect(() => {
    fetchStaffUsers();
  }, [searchTerm]);

  const fetchStaffUsers = async () => {
    setTableLoading(true);
    try {
      const res = await fetch(`https://metapi.emedhatechnologies.com/auth/api/api/staff?search=${encodeURIComponent(searchTerm)}`);
      const result = await res.json();
      if (result.success) {
        const formattedData = result.data.map(item => ({
          id: item.id, name: item.name, email: item.email, mobile: item.mobile, gender: item.gender, role: item.role,
          permissions: {
            crm: item.perm_crm === 1,
            students: item.perm_students === 1,
            courses: item.perm_courses === 1,
            fees: item.perm_fees === 1,
            exams: item.perm_exams === 1,
            notifications: item.perm_notifications === 1,
            analytics: item.perm_analytics === 1,
            access: item.perm_access === 1
          }
        }));
        setStaffList(formattedData);
      }
    } catch (err) {
      console.error("API link failure:", err);
    } finally {
      setTableLoading(false);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const endpoint = editingStaffId ? `https://metapi.emedhatechnologies.com/auth/api/api/staff/${editingStaffId}` : 'https://metapi.emedhatechnologies.com/auth/api/api/staff';
    const method = editingStaffId ? 'PUT' : 'POST';

    try {
      const res = await fetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const result = await res.json();
      if (result.success) {
        setIsModalOpen(false);
        fetchStaffUsers();
      } else {
        alert(result.message);
      }
    } catch (err) {
      console.error("API execution aborted:", err);
    }
  };

  const handleDeleteStaff = async (id) => {
    if (window.confirm("Are you sure you want to delete this staff member?")) {
      try {
        const res = await fetch(`https://metapi.emedhatechnologies.com/auth/api/api/staff/${id}`, { method: 'DELETE' });
        const result = await res.json();
        if (result.success) fetchStaffUsers();
      } catch (err) {
        console.error("Delete operation failure:", err);
      }
    }
  };

  const openAddModal = () => {
    setEditingStaffId(null);
    setFormData({
      name: '', email: '', mobile: '', gender: 'Male', role: 'counsellor', password: '',
      permissions: { crm: false, students: false, courses: false, fees: false, exams: false, notifications: false, analytics: false, access: false }
    });
    setIsModalOpen(true);
  };

  const openEditModal = (staff) => {
    setEditingStaffId(staff.id);
    setFormData({
      name: staff.name, email: staff.email, mobile: staff.mobile, gender: staff.gender, role: staff.role, password: '',
      permissions: { ...staff.permissions }
    });
    setIsModalOpen(true);
  };

  const totalPages = Math.ceil(staffList.length / itemsPerPage) || 1;
  const currentItems = staffList.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  return (
    <div>
      <div style={styles.tableHeaderControls}>
        <div style={styles.searchWrapper}>
          <Search size={16} style={styles.searchIcon} />
          <input type="text" placeholder="Search staff..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={styles.tableSearchInput} />
        </div>
        <button onClick={openAddModal} style={styles.addNewBtn}><Plus size={16} style={{ marginRight: '6px' }} /> Add Staff User</button>
      </div>

      {tableLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}><Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} color="#459bf2" /></div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.dataTable}>
            <thead>
              <tr>
                <th style={styles.tableTh}>Name</th>
                <th style={styles.tableTh}>Email</th>
                <th style={styles.tableTh}>Mobile</th>
                <th style={styles.tableTh}>Role</th>
                <th style={styles.tableTh}>Gender</th>
                <th style={styles.tableTh}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.map((staff) => (
                <tr key={staff.id} style={styles.tableTr}>
                  <td style={styles.tableTd}>{staff.name}</td>
                  <td style={styles.tableTd}>{staff.email}</td>
                  <td style={styles.tableTd}>{staff.mobile}</td>
                  <td style={styles.tableTd}>
                    <span style={{ ...styles.roleBadge, ...(staff.role === 'admin' ? styles.badgeAdmin : staff.role === 'accounts' ? styles.badgeAcct : styles.badgeCoun) }}>
                      {staff.role}
                    </span>
                  </td>
                  <td style={styles.tableTd}>{staff.gender}</td>
                  <td style={styles.tableTd}>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button onClick={() => openEditModal(staff)} style={styles.actionEditBtn}><Edit2 size={14} /></button>
                      <button onClick={() => handleDeleteStaff(staff.id)} style={styles.actionDeleteBtn}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={styles.paginationBar}>
        <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px' }}>Page {currentPage} of {totalPages}</span>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)} style={{ ...styles.pagBtn, ...(currentPage === 1 && styles.pagBtnDisabled) }}>Prev</button>
          <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)} style={{ ...styles.pagBtn, ...(currentPage === totalPages && styles.pagBtnDisabled) }}>Next</button>
        </div>
      </div>


    {/* FLOATING OVERLAY PERMISSIONS MODAL */}
      {isModalOpen && (
        <div style={styles.modalBackdrop}>
          <div style={styles.modalWrapperCard}>
            <div style={styles.modalHeader}>
              <h3 style={{ color: '#0b1528', margin: 0, fontWeight: 600 }}>
                {editingStaffId ? "Edit Staff Configurations" : "Provision New Staff Profile"}
              </h3>
              <button onClick={() => setIsModalOpen(false)} style={styles.modalCloseX}><X size={18} /></button>
            </div>
            
            {/* Scrollable container wraps the form body contents */}
            <div style={styles.modalScrollBody}>
              <form onSubmit={handleFormSubmit} style={styles.modalForm}>
                <div style={styles.formSplitGrid}>
                  <div style={styles.fieldContainer}><label style={styles.fieldLabel}>Full Name</label><input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} style={styles.formInput} /></div>
                  <div style={styles.fieldContainer}><label style={styles.fieldLabel}>Email Address</label><input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} style={styles.formInput} /></div>
                  <div style={styles.fieldContainer}><label style={styles.fieldLabel}>Mobile Number</label><input type="text" required value={formData.mobile} onChange={(e) => setFormData({ ...formData, mobile: e.target.value })} style={styles.formInput} /></div>
                  <div style={styles.fieldContainer}><label style={styles.fieldLabel}>Gender</label><select value={formData.gender} onChange={(e) => setFormData({ ...formData, gender: e.target.value })} style={styles.formSelect}><option>Male</option><option>Female</option></select></div>
                  <div style={styles.fieldContainer}><label style={styles.fieldLabel}>Functional Role</label><select value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })} style={styles.formSelect}><option value="admin">Admin</option><option value="counsellor">Counsellor</option><option value="accounts">Accounts</option></select></div>
                  <div style={styles.fieldContainer}><label style={styles.fieldLabel}>Password</label>
                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                      <input type={showPassword ? "text" : "password"} required={!editingStaffId} placeholder={editingStaffId ? "Leave blank to preserve" : ""} value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} style={styles.formInput} />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} style={styles.eyeToggleBtn}>{showPassword ? <EyeOff size={16} /> : <Eye size={16} />}</button>
                    </div>
                  </div>
                </div>

                <div>
                  <label style={styles.fieldLabel}>Structural Permissions Settings Matrix</label>
                  <div style={styles.permissionsCheckboxGrid}>
                    {modulesCheckboxConfig.map(m => (
                      <label key={m.id} style={styles.checkboxWrapper}>
                        <input type="checkbox" checked={formData.permissions[m.id]} onChange={(e) => setFormData({ ...formData, permissions: { ...formData.permissions, [m.id]: e.target.checked } })} />
                        <span style={{ color: '#334155', fontSize: '13.5px', fontWeight: '500' }}>{m.name}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div style={styles.formActionDock}>
                  <button type="button" onClick={() => setIsModalOpen(false)} style={styles.formCancelBtn}>Cancel</button>
                  <button type="submit" style={styles.formSaveBtn}>Submit Changes</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      <style dangerouslySetInnerHTML={{__html: `
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        input[type="checkbox"] { accent-color: #459bf2; width: 16px; height: 16px; cursor: pointer; }
      `}} />
    </div>
  );
}

const styles = {
  tableHeaderControls: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' },
  searchWrapper: { position: 'relative', display: 'flex', alignItems: 'center', flex: 1, maxWidth: '380px', width: '100%' },
  searchIcon: { position: 'absolute', left: '14px', color: '#94a3b8' },
  tableSearchInput: { width: '100%', padding: '12px 14px 12px 42px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', color: '#0f172a', fontSize: '14px', outline: 'none', transition: 'all 0.2s ease' },
  addNewBtn: { background: 'linear-gradient(90deg, #459bf2 0%, #1c6ec9 100%)', border: 'none', borderRadius: '12px', color: '#fff', padding: '12px 20px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', boxShadow: '0 4px 12px rgba(69, 155, 242, 0.25)' },
  dataTable: { width: '100%', borderCollapse: 'collapse', textAlign: 'left', marginTop: '8px' },
  tableTh: { padding: '14px 16px', borderBottom: '2px solid #edf2f7', color: '#0b1528', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.75px', fontWeight: '600' },
  tableTr: { borderBottom: '1px solid #f1f5f9', transition: 'background 0.2s ease' },
  tableTd: { padding: '16px', color: '#334155', fontSize: '14px' },
  roleBadge: { padding: '5px 12px', borderRadius: '8px', fontSize: '11.5px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' },
  badgeAdmin: { background: 'rgba(227, 5, 4, 0.06)', border: '1px solid rgba(227, 5, 4, 0.15)', color: '#e30504' },
  badgeAcct: { background: 'rgba(69, 155, 242, 0.06)', border: '1px solid rgba(69, 155, 242, 0.15)', color: '#1c6ec9' },
  badgeCoun: { background: '#f1f5f9', border: '1px solid #e2e8f0', color: '#475569' },
  actionEditBtn: { background: '#f0f7ff', border: 'none', borderRadius: '8px', color: '#459bf2', padding: '9px', cursor: 'pointer', transition: 'all 0.2s' },
  actionDeleteBtn: { background: '#fff5f5', border: 'none', borderRadius: '8px', color: '#e30504', padding: '9px', cursor: 'pointer', transition: 'all 0.2s' },
  paginationBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px', flexWrap: 'wrap', gap: '12px' },
  pagBtn: { background: '#459bf2', border: '1px solid #e2e8f0', borderRadius: '8px', color: '#f8fafc', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', fontWeight: '500', transition: 'all 0.2s' },
  pagBtnActive: { background: '#459bf2', borderColor: '#459bf2', color: '##f8fafc', fontWeight: '600' },
  pagBtnDisabled: { opacity: 0.4, cursor: 'not-allowed', background: '#475569' },
  
  // Luxury Light View Form Card Modals Layout Spec Sheet
  modalBackdrop: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.3)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' },
  modalWrapperCard: { background: '#ffffff', border: '1px solid rgba(255,255,255,0.8)', borderRadius: '24px', width: '100%', maxWidth: '640px', maxHeight: '90vh', padding: '28px', boxShadow: '0 25px 50px -12px rgba(15, 23, 42, 0.15)', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '16px', borderBottom: '1px solid #e2e8f0' },
  modalCloseX: { background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', display: 'flex', alignItems: 'center' },
  modalScrollBody: { flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '20px 4px 4px 4px', marginTop: '4px' },
  modalForm: { display: 'flex', flexDirection: 'column', gap: '20px' },
  formSplitGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '18px' },
  fieldContainer: { display: 'flex', flexDirection: 'column', gap: '6px' },
  fieldLabel: { color: '#64748b', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.75px' },
  formInput: { width: '100%', padding: '12px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', color: '#0f172a', fontSize: '14px', outline: 'none', boxSizing: 'border-box' },
  formSelect: { padding: '12px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', color: '#0f172a', fontSize: '14px', outline: 'none' },
  eyeToggleBtn: { position: 'absolute', right: '12px', background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer' },
  permissionsCheckboxGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', background: '#f8fafc', padding: '18px', borderRadius: '14px', border: '1px solid #e2e8f0', marginTop: '6px' },
  checkboxWrapper: { display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' },
  formActionDock: { display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px', borderTop: '1px solid #e2e8f0', paddingTop: '16px' },
  formCancelBtn: { background: 'transparent', border: '1px solid #e2e8f0', color: '#64748b', padding: '12px 22px', borderRadius: '10px', cursor: 'pointer', fontWeight: '500' },
  formSaveBtn: { background: 'linear-gradient(90deg, #459bf2 0%, #1c6ec9 100%)', border: 'none', color: '#fff', padding: '12px 24px', borderRadius: '10px', fontWeight: '600', cursor: 'pointer', boxShadow: '0 4px 12px rgba(69, 155, 242, 0.2)' }
};